import { useState, useMemo, useEffect } from 'react';

interface SearchOptions {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const useSearch = (
  items: any[], 
  options: SearchOptions = {},
  initialSearchTerm: string = ''
) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  
  // When initialSearchTerm changes, update searchTerm
  useEffect(() => {
    if (initialSearchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);
  
  const filteredItems = useMemo(() => {
    // First filter
    let results = items.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Only sort if explicitly configured - otherwise preserve API response order
    if (options.sortBy) {
      const sortBy = options.sortBy;
      const sortDirection = options.sortDirection || 'asc';

      results.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];

        // Handle numeric sorting
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
        }

        // Handle string sorting (including null/undefined values)
        const aString = String(aValue || '').toLowerCase();
        const bString = String(bValue || '').toLowerCase();
        return sortDirection === 'desc' 
          ? bString.localeCompare(aString) 
          : aString.localeCompare(bString);
      });
    }
    // If no sortBy configured, preserve original API response order

    return results;
  }, [items, searchTerm, options.sortBy, options.sortDirection]);

  return { searchTerm, setSearchTerm, filteredItems };
}; 